import { useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { UserPlusIcon, XIcon } from "lucide-react";

import { canManageChallenge } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useAppSelector } from "store";

import { useIdea } from "components/ideas/hooks/useIdea";
import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";
import { UserChooser } from "components/lib/Choosers";
import type {
  ChoosableUser,
  EmailInviteeUser,
} from "components/lib/Choosers/types";
import type { IdeaPreviewProjectBoard } from "components/lib/Ideas/IdeaPreview/types";

const isEmailInvitee = (user: ChoosableUser): user is EmailInviteeUser =>
  "isEmailInvitee" in user && user.isEmailInvitee === true;

type PromptFinalOutcomeProps = { ideaId: string };

export const PromptFinalOutcome = ({ ideaId }: PromptFinalOutcomeProps) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector((state) => state.user);
  const [idea] = useIdea();
  const [sending, setSending] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const canManage =
    !!idea &&
    idea._id === ideaId &&
    !!canManageChallenge(currentUser, idea.ownerChallenge);
  const recipientsQuery = useQuery(
    initUseQuery(api.ideas.getFinalOutcomePrompts, [ideaId], {
      isEnabled: canManage,
    }),
  );

  if (!idea || !canManage) {
    return null;
  }

  const recipients = recipientsQuery.data?.recipients ?? [];
  const setRecipients = (
    data: OpenAPI.GET<"/ideas/{idea_id}/finalOutcome/prompt">["response"],
  ) => api.setQueryData(api.ideas.getFinalOutcomePrompts, [ideaId], () => data);
  // Only project assignees can complete the final outcome when signed in, anyone else is sent a link by email
  const board = idea.projectManagement?.boards?.find(
    (b) => b.forId === idea.challenge,
  ) as IdeaPreviewProjectBoard | undefined;
  const assigneeIds = board?.assignees ?? [];
  const assigneeUsers = board?.assigneeUsers ?? [];

  const removeRecipient = (recipient: string) => {
    setRemoving(recipient);
    api.ideas.removeFinalOutcomePrompt(
      idea._id,
      { recipient },
      (data) => {
        setRecipients(data);
        setRemoving(null);
        toast.success(t("ideas.impact.finalOutcome.prompt.removed"));
      },
      () => {
        setRemoving(null);
        toast.error(t("ideas.impact.finalOutcome.prompt.removeFailed"));
      },
    );
  };

  return (
    <>
      <Divider style={{ margin: "6px 0 2px" }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <H5 style={{ margin: 0 }}>
          {t("ideas.impact.finalOutcome.prompt.title")}
          <Subheader>
            {t("ideas.impact.finalOutcome.prompt.description")}
          </Subheader>
        </H5>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f7f7f7",
            borderRadius: 5,
            padding: 5,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {recipients.map(({ recipient, email, ownerUser }) => {
              const promptedUser = email
                ? ({ _id: recipient, profile: { fullName: email } } as ChipUser)
                : ownerUser;
              if (!promptedUser) {
                return null;
              }
              return (
                <UserChip
                  key={recipient}
                  user={promptedUser as ChipUser}
                  compact
                  actionIcon={XIcon}
                  actionTooltip={t("ideas.impact.finalOutcome.prompt.remove")}
                  actionClick={
                    removing === recipient
                      ? undefined
                      : () => removeRecipient(recipient)
                  }
                />
              );
            })}
          </div>
          <UserChooser
            enabledFeatures={{ search: true, invite: true }}
            isLoading={sending}
            clearOnComplete
            subtitle={t("ideas.impact.finalOutcome.prompt.subtitle")}
            confirm={t("ideas.impact.finalOutcome.prompt.confirm")}
            trigger={
              <Button size="small" style={{ minWidth: "max-content" }}>
                <p>
                  <Icon icon={UserPlusIcon} />{" "}
                  {t("ideas.impact.finalOutcome.prompt.button")}
                </p>
              </Button>
            }
            searchFunction={(term, cb) => {
              const search = term.trim().toLowerCase();
              cb(
                assigneeUsers.filter((user) =>
                  user.profile?.fullName?.toLowerCase().includes(search),
                ),
              );
            }}
            onComplete={(chosen) => {
              const userIds: string[] = [];
              const emails: string[] = [];
              chosen.forEach((user) => {
                if (isEmailInvitee(user)) {
                  emails.push(user._id);
                } else if (user._id && assigneeIds.includes(user._id)) {
                  userIds.push(user._id);
                } else {
                  // An existing account found by email that isn't assigned, send them a direct link instead
                  const address = user.emails?.[0]?.address;
                  if (address) {
                    emails.push(address);
                  }
                }
              });
              if (!userIds.length && !emails.length) {
                return;
              }
              setSending(true);
              api.ideas.promptFinalOutcome(
                idea._id,
                { users: userIds, emails },
                (data) => {
                  setRecipients(data);
                  setSending(false);
                  toast.success(t("ideas.impact.finalOutcome.prompt.sent"));
                },
                () => {
                  setSending(false);
                  toast.error(t("ideas.impact.finalOutcome.prompt.failed"));
                },
              );
            }}
          />
        </div>
      </div>
    </>
  );
};
